/**
 * WorkerFlagsAdmin — Estado y coherencia del Cloudflare Worker
 *
 * Panel de solo lectura que:
 * 1. Muestra el __TEMPLATE_CONFIG__ actual inyectado por el worker
 * 2. Documenta qué variables controla el worker y cómo cambiarlas
 * 3. Sirve como "fuente de verdad" de qué hace el edge en producción
 *
 * Las variables que se pueden cambiar desde el admin (via DB) tienen un link
 * a la sección correspondiente del admin.
 * Las variables que solo se pueden cambiar en Cloudflare tienen instrucciones claras.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Eye,
  Gift,
  Globe,
  Info,
  MapPin,
  RefreshCw,
  Shield,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BRAND_SITE_URL, BRAND_SITE_HOST } from "@/lib/brandConfig";

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface TemplateConfig {
  country?: string;
  templateName?: string;
  bioLayout?: string | null;
  language?: string;
  province?: string;
  provinceDetectionMethod?: string;
  usState?: string;
  usStateDetectionMethod?: string;
  whatsappBroadcast?: boolean;
  hideCta?: boolean;
  showWishlist?: boolean;
  arPremiumWindowActive?: boolean;
  killSwitchActive?: boolean;
  cfCountry?: string;
  timezone?: string;
  city?: string;
  regionCode?: string;
  region?: string;
  latitude?: string | null;
  longitude?: string | null;
  postalCode?: string;
  colo?: string | null;
  asn?: number | null;
  continent?: string | null;
  detectionMethod?: string;
  vpnDetected?: boolean;
  asOrganization?: string;
}

// Acceso a __TEMPLATE_CONFIG__ inyectado por el worker en window
// (declarado globalmente en otro módulo; aquí lo casteamos localmente)
type WindowWithTemplateConfig = typeof window & { __TEMPLATE_CONFIG__?: TemplateConfig };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function ValueBadge({ value }: { value: unknown }) {
  if (value === undefined || value === null) {
    return <span className="text-xs text-muted-foreground italic">—</span>;
  }
  if (typeof value === "boolean") {
    return value ? (
      <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
        <CheckCircle2 className="h-3 w-3" /> true
      </Badge>
    ) : (
      <Badge variant="outline" className="text-muted-foreground">false</Badge>
    );
  }
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
      {String(value)}
    </code>
  );
}

function Row({ label, value, desc }: { label: string; value: unknown; desc?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md px-3 py-2 odd:bg-muted/30">
      <div className="min-w-0">
        <p className="text-sm font-medium font-mono">{label}</p>
        {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
      </div>
      <div className="shrink-0">
        <ValueBadge value={value} />
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function WorkerFlagsAdmin() {
  const [config, setConfig] = useState<TemplateConfig | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setConfig((window as WindowWithTemplateConfig).__TEMPLATE_CONFIG__ ?? null);
  }, [refreshKey]);

  const hasConfig = config !== null;
  const isKillSwitchActive = config?.killSwitchActive === true;
  const isHideCta = config?.hideCta === true;
  const isShowWishlist = config?.showWishlist === true;

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <AdminPageHeader
        description="Qué inyecta el Worker en prod. Overrides DB → CTAs. Template por país → Home por país."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Refrescar
          </Button>
        }
      />

      {/* Estado actual del worker */}
      <Card className={`border-2 ${hasConfig ? "border-green-300 dark:border-green-800" : "border-amber-300 dark:border-amber-800"}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            {hasConfig ? (
              <Wifi className="h-5 w-5 text-green-600" />
            ) : (
              <WifiOff className="h-5 w-5 text-amber-500" />
            )}
            <div>
              <CardTitle className="text-base">
                {hasConfig ? "Worker activo — config inyectada" : "Sin config del worker"}
              </CardTitle>
              <CardDescription>
                {hasConfig
                  ? "window.__TEMPLATE_CONFIG__ detectada en esta página"
                  : "El worker no inyectó config (desarrollo local, test sin worker, o worker desconectado)"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Kill switch alert */}
      {isKillSwitchActive && (
        <Alert className="border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-700 dark:text-red-400">Kill switch ACTIVO</AlertTitle>
          <AlertDescription className="text-red-600/80 dark:text-red-400/80">
            <code>FORCE_BASE_TEMPLATE = true</code> en el worker. Todos los visitantes ven BaseTemplate
            independientemente de su país. Para desactivarlo, editar <code>worker.js</code> o el env var
            en Cloudflare Dashboard.
          </AlertDescription>
        </Alert>
      )}

      {/* Config actual del worker */}
      {hasConfig && (
        <Card className="border-border/80 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Config inyectada por el worker</CardTitle>
            </div>
            <CardDescription>
              Valores de <code className="text-xs">window.__TEMPLATE_CONFIG__</code> para <strong>esta sesión</strong>.
              Cambian según el país/provincia del visitante real. En el admin pueden verse diferente a producción.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-0.5">
            <div className="grid grid-cols-1 gap-0.5 md:grid-cols-2">
              <div>
                <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Geo</p>
                <Row label="country"                  value={config.country}                  desc="País detectado (ISO2)" />
                <Row label="cfCountry"                value={config.cfCountry}               desc="País directo de Cloudflare" />
                <Row label="province"                 value={config.province}                desc="Provincia AR (si aplica)" />
                <Row label="provinceDetectionMethod"  value={config.provinceDetectionMethod} desc="Método de detección de provincia" />
                <Row label="usState"                  value={config.usState}                 desc="Estado US (si aplica)" />
                <Row label="city"                     value={config.city}                    />
                <Row label="regionCode"               value={config.regionCode}              />
                <Row label="timezone"                 value={config.timezone}                />
                <Row label="continent"                value={config.continent}               />
                <Row label="colo"                     value={config.colo}                    desc="Datacenter CF más cercano" />
                <Row label="detectionMethod"          value={config.detectionMethod}         />
              </div>
              <div>
                <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Template y CTA</p>
                <Row label="templateName"             value={config.templateName}            desc="Template renderizado" />
                <Row label="language"                 value={config.language}                />
                <Row label="hideCta"                  value={config.hideCta}                 desc="true = ningún botón CTA" />
                <Row label="showWishlist"             value={config.showWishlist}            desc="true = botón wishlist" />
                <Row label="whatsappBroadcast"        value={config.whatsappBroadcast}       desc="true = botón WhatsApp broadcast" />
                <Row label="arPremiumWindowActive"    value={config.arPremiumWindowActive}   desc="Ventana premium AR (3-5 AM ART)" />
                <Row label="killSwitchActive"         value={config.killSwitchActive}        desc="FORCE_BASE_TEMPLATE activo" />
                <p className="mb-1 mt-3 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Red / VPN</p>
                <Row label="vpnDetected"              value={config.vpnDetected}             desc="Señal de VPN/datacenter" />
                <Row label="asOrganization"           value={config.asOrganization}          />
                <Row label="asn"                      value={config.asn}                     />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estado de CTA coherente */}
      <Card className="border-border/80 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-purple-500" />
            <CardTitle className="text-base">Estado CTA actual</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            {!hasConfig ? (
              <span className="text-sm text-muted-foreground">Sin config del worker — no se puede determinar</span>
            ) : isHideCta ? (
              <Badge variant="outline" className="gap-1.5 text-muted-foreground">
                Sin botón CTA (hideCta)
              </Badge>
            ) : isShowWishlist ? (
              <Badge variant="secondary" className="gap-1.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                <Gift className="h-3.5 w-3.5" /> Wishlist activo
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1.5 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                Cumpleaños activo
              </Badge>
            )}
            <Link to="/admin/wishlist-config">
              <Button variant="outline" size="sm" className="gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                Editar CTA por región
              </Button>
            </Link>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            El worker lee la config de CTAs desde <code className="rounded bg-muted px-1">/api/wishlist-config</code> (DB).
            Cambios en "CTA por región" se propagan al worker en ~60 segundos.
          </p>
        </CardContent>
      </Card>

      <Separator />

      {/* Variables globales del worker */}
      <div>
        <h2 className="mb-4 flex items-center gap-2 text-base font-semibold">
          <Zap className="h-4 w-4 text-amber-500" />
          Variables globales del worker
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Estas variables controlan el comportamiento del worker para <strong>todos los visitantes</strong>.
          No se pueden cambiar desde el admin sin redeploy — se modifican en <strong>Cloudflare Dashboard</strong>.
        </p>

        <div className="space-y-3">

          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono font-semibold">FORCE_BASE_TEMPLATE</code>
                    <Badge variant="outline" className="text-xs">worker.js · línea ~128</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Kill switch global. <code>true</code> → todos los visitantes ven BaseTemplate, sin importar el país.
                    Activa <code>killSwitchActive</code> en el template config.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <strong>Cómo cambiar:</strong> Editar <code>worker.js</code> + redeploy, o en Cloudflare Dashboard →
                    Workers → tu worker → Settings → Variables → <code>FORCE_BASE_TEMPLATE</code>
                  </p>
                </div>
                <a
                  href="https://dash.cloudflare.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm" className="shrink-0 gap-1.5">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Cloudflare
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/40 dark:border-amber-800 dark:bg-amber-950/20">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono font-semibold">VERCEL_SITE_ORIGIN</code>
                    <Badge className="gap-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800 text-xs">
                      ⚠️ Requerida
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Origen del subrequest interno a <code>/api/wishlist-config</code>.
                    Aunque <code>{BRAND_SITE_HOST}</code> pasa por el worker, el path <code>/api/*</code> hace
                    pass-through directo al origin Railway sin re-ejecutar lógica de CTA — no hay recursión real.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <strong>Valor correcto:</strong>{" "}
                    <code className="rounded bg-muted px-1 select-all">{BRAND_SITE_URL}</code>
                    <br />
                    <strong>Dónde configurar:</strong> <code>wrangler.toml</code> → <code>[vars] VERCEL_SITE_ORIGIN</code> + <code>wrangler deploy</code>,
                    o Cloudflare Dashboard → Workers → Settings → Variables
                  </p>
                </div>
                <a
                  href="https://dash.cloudflare.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm" className="shrink-0 gap-1.5">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Cloudflare
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono font-semibold">GLOBAL_HIDE_HERO_CTA</code>
                    <Badge variant="outline" className="text-xs">env var · Cloudflare</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Oculta todos los botones CTA del hero en BaseTemplate. El Worker aplica{" "}
                    <strong>esta env</strong> <em>o</em> el interruptor en{" "}
                    <Link to="/admin/wishlist-config" className="underline font-medium">
                      Botón CTA por región
                    </Link>{" "}
                    (<code className="text-xs">site_settings.hide_global_hero_cta</code>): si cualquiera está
                    activo, no se muestran los CTAs.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <strong>Cloudflare (sin redeploy de código):</strong> Dashboard → Workers → Variables →{" "}
                    <code>GLOBAL_HIDE_HERO_CTA = true</code>
                    <br />
                    <strong>Admin (rápido, segundos):</strong> interruptor &quot;Hero: ocultar todos los CTA&quot; en la página enlazada arriba.
                  </p>
                </div>
                <a
                  href="https://dash.cloudflare.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm" className="shrink-0 gap-1.5">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Cloudflare
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono font-semibold">WHATSAPP_BROADCAST_DISABLED</code>
                    <Badge variant="outline" className="text-xs">worker.js · línea ~155</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Desactiva el botón de WhatsApp broadcast para todas las provincias AR.
                    Actualmente: <code>true</code> (desactivado).
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <strong>Cómo cambiar:</strong> Editar <code>WHATSAPP_BROADCAST_DISABLED = false</code> en <code>worker.js</code> + redeploy.
                    Las provincias se configuran con <code>WHATSAPP_BROADCAST_PROVINCES</code>.
                  </p>
                </div>
                <a
                  href="https://dash.cloudflare.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm" className="shrink-0 gap-1.5">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Cloudflare
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono font-semibold">COUNTRY_TEMPLATE_MAP</code>
                    <Badge variant="outline" className="text-xs">worker.js · línea ~44</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Mapa de país → template. Define qué template ve cada país (BaseTemplate, BioTemplatePremiumSimple, etc.).
                    Argentina siempre usa BaseTemplate independientemente de este mapa.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <strong>Cómo cambiar:</strong> Editar <code>COUNTRY_TEMPLATE_MAP</code> en <code>worker.js</code> + redeploy.
                    El archivo <code>src/config/workerCountryTemplateMap.ts</code> es un mirror manual para el frontend.
                  </p>
                </div>
                <Badge variant="outline" className="shrink-0 text-muted-foreground">Solo código</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono font-semibold">AR/GLOBALLY_BLOCKED_PATHS</code>
                    <Badge variant="outline" className="text-xs">worker.js · línea ~259</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Rutas bloqueadas (fake 404) para Argentina o globalmente.
                    Ej: <code>/invitacion-premium</code> bloqueado en AR.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <strong>Cómo cambiar:</strong> Editar <code>GLOBALLY_BLOCKED_PATHS</code> / <code>AR_BLOCKED_PATHS</code> en <code>worker.js</code> + redeploy.
                  </p>
                </div>
                <Badge variant="outline" className="shrink-0 text-muted-foreground">Solo código</Badge>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>

      <Separator />

      {/* Variables controladas desde el admin */}
      <div>
        <h2 className="mb-4 flex items-center gap-2 text-base font-semibold">
          <Shield className="h-4 w-4 text-green-500" />
          Variables controladas desde el admin (vía DB)
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Estas variables se pueden cambiar desde el admin sin redeploy. El worker las lee
          desde la base de datos con un cache de ~60 segundos.
        </p>

        <div className="space-y-3">
          <Card className="border-green-200 bg-green-50/40 dark:border-green-800 dark:bg-green-950/20">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono font-semibold">CTA por provincia AR / país</code>
                    <Badge className="gap-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800">
                      <CheckCircle2 className="h-3 w-3" /> Admin
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Configuración de <code>hideCta</code> / <code>showWishlist</code> / botón cumpleaños por provincia AR y por país.
                    El worker lee <code>/api/wishlist-config</code> (tabla <code>wishlist_geo_config</code> en PostgreSQL)
                    con cache de 60 s.
                  </p>
                </div>
                <Link to="/admin/wishlist-config">
                  <Button size="sm" className="shrink-0 gap-1.5">
                    <Gift className="h-3.5 w-3.5" />
                    Editar
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/40 dark:border-green-800 dark:bg-green-950/20">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono font-semibold">Site settings (BaseTemplate)</code>
                    <Badge className="gap-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800">
                      <CheckCircle2 className="h-3 w-3" /> Admin
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Header URL, logo, banner, Sobre mí, galería, footer. Se leen desde
                    la tabla <code>site_settings</code> sin pasar por el worker.
                  </p>
                </div>
                <Link to="/admin/site-settings">
                  <Button size="sm" variant="outline" className="shrink-0 gap-1.5">
                    <Globe className="h-3.5 w-3.5" />
                    Editar
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Alert className="border-border/60 bg-muted/40">
        <Info className="h-4 w-4" />
        <AlertTitle>¿Cómo funciona el flujo completo?</AlertTitle>
        <AlertDescription className="mt-2 space-y-2 text-sm text-muted-foreground">
          <ol className="list-decimal list-inside space-y-1">
            <li>Visitante accede a <code>{BRAND_SITE_HOST}</code></li>
            <li>Cloudflare intercepta → worker detecta país/provincia</li>
            <li>Worker consulta <code>/api/wishlist-config</code> (cache 60 s) → obtiene config de CTA desde DB</li>
            <li>Worker inyecta <code>window.__TEMPLATE_CONFIG__</code> con todos los flags en el HTML</li>
            <li>React lee <code>__TEMPLATE_CONFIG__</code> → renderiza el template y CTA correcto</li>
          </ol>
        </AlertDescription>
      </Alert>
    </div>
  );
}
